import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";

export interface IChatMessage {
  id: string;
  author: "user" | "ai";
  content: string;
  timestamp: string;
  isFile: boolean;
}

export interface IChat {
  id: string;
  firstMsg: string;
  messages: IChatMessage[];
}

interface ChatContextValue {
  chats: IChat[];
  addChat: (firstMsg: string, isFile?: boolean) => string; // возвращаем id
  addMessage: (chatId: string, author: "user" | "ai", content: string, isFile?: boolean) => void;
}

const uuid = () => Date.now().toString() + "-" + Math.random().toString(36).slice(2, 8);

const DEFAULT_CHATS: IChat[] = [
  "Опиши УТП проекта AIVOX?",
  "Какие решения для запуска экосистемы?",
  "Какой бюджет у проекта Aivox?",
  "Опиши проект AIVOX?",
  "Основные выводы по проекту Cozystack (Open-Source, Партнерство)?",
  "Проекты для B2B?",
].map((msg) => ({
  id: uuid(),
  firstMsg: msg,
  messages: [
    {
      id: uuid(),
      author: "user",
      content: msg,
      timestamp: new Date().toISOString(),
      isFile: false,
    },
  ],
}));


const ChatContext = createContext<ChatContextValue | undefined>(undefined);

export const ChatProvider = ({ children }: { children: ReactNode }) => {
  const [chats, setChats] = useState<IChat[]>(() => {
    try {
      const saved = localStorage.getItem("chats");
      return saved ? JSON.parse(saved) : DEFAULT_CHATS;
    } catch {
      return DEFAULT_CHATS;
    }
  })

  const addChat = (firstMsg: string, isFile = false): string => {
    const newChat: IChat = {
      id: uuid(),
      firstMsg,
      messages: [
        {
          id: uuid(),
          author: "user",
          content: firstMsg,
          timestamp: new Date().toISOString(),
          isFile,
        },
      ],
    };
    setChats((prev) => [...prev, newChat]);
    return newChat.id;
  };

  const addMessage = (chatId: string, author: "user" | "ai", content: string, isFile = false) => {
    setChats((prev) =>
      prev.map((chat) =>
        chat.id === chatId
          ? {
            ...chat,
            messages: [
              ...chat.messages,
              {
                id: uuid(),
                author,
                content,
                timestamp: new Date().toISOString(),
                isFile: author === "user" ? isFile : false
              },
            ],
          }
          : chat
      )
    );
  };

  useEffect(() => {
    localStorage.setItem("chats", JSON.stringify(chats));
  }, [chats]);

  return (
    <ChatContext.Provider value={{ chats, addChat, addMessage }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = (): ChatContextValue => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChat must be used inside ChatProvider");
  return ctx;
};
